import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  COMPETENCY_PERMS as P,
  useCompetencyAction,
  useCompetencyCan,
  useCompetencyEmployeeFile,
} from '@/hooks/useCompetency';
import {
  competencyService,
  type CertificationRecord,
  type CompetencyAssessment,
  type CompetencyMatrix,
  type TrainingRecord,
} from '@/services/competency.service';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, COMPETENCY_LEVELS, GAP_COLOR, GRADE_COLOR, STATUS_COLOR } from './competencyDisplay';

type Editing<T> = { record: Partial<T> | null } | null;

const toDate = (v?: string | null) => (v ? dayjs(v) : undefined);
const fromDate = (v?: dayjs.Dayjs | null) => (v ? v.format('YYYY-MM-DD') : null);

const CompetencyEmployeePage: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useCompetencyEmployeeFile(employeeId);
  const canCompetency = useCompetencyCan(P.create);
  const canTraining = useCompetencyCan(P.trainingCreate, P.trainingManage);
  const canCert = useCompetencyCan(P.certificationCreate);
  const [competency, setCompetency] = useState<Editing<CompetencyMatrix>>(null);
  const [training, setTraining] = useState<Editing<TrainingRecord>>(null);
  const [cert, setCert] = useState<Editing<CertificationRecord>>(null);
  const [competencyForm] = Form.useForm();
  const [trainingForm] = Form.useForm();
  const [certForm] = Form.useForm();

  const saveCompetency = useCompetencyAction((v: Partial<CompetencyMatrix>) => competencyService.upsertCompetency(v), 'Competency saved');
  const removeCompetency = useCompetencyAction((id: string) => competencyService.deleteCompetency(id), 'Competency deleted');
  const saveTraining = useCompetencyAction(
    (v: { id?: string; data: Partial<TrainingRecord> }) =>
      v.id ? competencyService.updateTraining(v.id, v.data) : competencyService.createTraining(v.data),
    'Training record saved'
  );
  const removeTraining = useCompetencyAction((id: string) => competencyService.deleteTraining(id), 'Training record deleted');
  const saveCert = useCompetencyAction(
    (v: { id?: string; data: Partial<CertificationRecord> }) =>
      v.id ? competencyService.updateCertification(v.id, v.data) : competencyService.createCertification(v.data),
    'Certificate saved'
  );
  const removeCert = useCompetencyAction((id: string) => competencyService.deleteCertification(id), 'Certificate deleted');

  if (isLoading) return <Spin style={{ margin: 48 }} />;
  if (error || !data) return <Alert type="error" showIcon style={{ margin: 24 }} message="Could not load the employee's competency file" />;
  const e = data.employee;

  const openCompetency = (r: Partial<CompetencyMatrix> | null) => {
    competencyForm.resetFields();
    competencyForm.setFieldsValue({
      required_level: 'Basic',
      current_level: 'Not Assessed',
      assessment_result: 'Under Assessment',
      ...r,
      last_assessed_date: toDate(r?.last_assessed_date),
    });
    setCompetency({ record: r });
  };
  const openTraining = (r: Partial<TrainingRecord> | null) => {
    trainingForm.resetFields();
    trainingForm.setFieldsValue({
      status: 'Completed',
      result: 'N/A',
      training_duration_hours: 0,
      ...r,
      actual_start_date: toDate(r?.actual_start_date ?? r?.scheduled_start_date),
      actual_end_date: toDate(r?.actual_end_date ?? r?.scheduled_end_date),
    });
    setTraining({ record: r });
  };
  const openCert = (r: Partial<CertificationRecord> | null) => {
    certForm.resetFields();
    certForm.setFieldsValue({ is_lifetime: false, status: 'Active', ...r, issue_date: toDate(r?.issue_date), expiry_date: toDate(r?.expiry_date) });
    setCert({ record: r });
  };

  const competencyColumns: ColumnsType<CompetencyMatrix> = [
    { title: 'Code', dataIndex: 'competency_code', width: 110 },
    { title: 'Competency', dataIndex: 'competency_name' },
    { title: 'Required', dataIndex: 'required_level', render: (v) => <Tag>{v}</Tag> },
    { title: 'Current', dataIndex: 'current_level', render: (v) => <Tag color="blue">{v}</Tag> },
    { title: 'Result', dataIndex: 'assessment_result' },
    { title: 'Compliant', dataIndex: 'is_compliant', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag> },
    {
      title: '',
      width: 90,
      render: (_, r) =>
        canCompetency ? (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openCompetency(r)} />
            <Popconfirm title="Delete this competency?" onConfirm={() => removeCompetency.mutate(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  const trainingColumns: ColumnsType<TrainingRecord & { training_session_id?: string | null; attendance_status?: string | null }> = [
    { title: 'Code', dataIndex: 'training_code', width: 110 },
    { title: 'Training', dataIndex: 'training_name' },
    { title: 'Date', render: (_, r) => formatDate(r.actual_start_date || r.scheduled_start_date) || '-', width: 110 },
    { title: 'Trainer', dataIndex: 'trainer_name' },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    { title: 'Result', dataIndex: 'result' },
    {
      title: 'Session',
      dataIndex: 'training_session_id',
      render: (v) => (v ? <a onClick={() => navigate(`${COMPETENCY_BASE}/sessions/${v}`)}>Open</a> : '-'),
    },
    {
      title: '',
      width: 90,
      render: (_, r) =>
        canTraining && !r.training_session_id ? (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openTraining(r)} />
            <Popconfirm title="Delete this training record?" onConfirm={() => removeTraining.mutate(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  const certColumns: ColumnsType<CertificationRecord> = [
    { title: 'Certificate', dataIndex: 'certification_name' },
    { title: 'Type', dataIndex: 'certification_type' },
    { title: 'Issued by', dataIndex: 'issuing_body' },
    { title: 'Number', dataIndex: 'certificate_number' },
    { title: 'Issued', dataIndex: 'issue_date', render: (v) => formatDate(v) || '-' },
    { title: 'Expires', dataIndex: 'expiry_date', render: (v, r) => (r.is_lifetime ? 'Lifetime' : formatDate(v) || '-') },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: '',
      width: 90,
      render: (_, r) =>
        canCert ? (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openCert(r)} />
            <Popconfirm title="Delete this certificate?" onConfirm={() => removeCert.mutate(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  const assessmentColumns: ColumnsType<CompetencyAssessment> = [
    {
      title: 'Assessment',
      dataIndex: 'assessment_number',
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/assessments/${r.id}`)}>{v}</a>,
    },
    { title: 'Type', dataIndex: 'assessment_type' },
    { title: 'Date', dataIndex: 'assessment_date', render: (v) => formatDate(v) || '-' },
    { title: 'Score', dataIndex: 'total_score' },
    { title: 'Grade', dataIndex: 'grade', render: (v) => (v ? <Tag color={GRADE_COLOR[v]}>{v}</Tag> : '-') },
    { title: 'Recommendation', dataIndex: 'recommendation' },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    { title: 'HR outcome', dataIndex: 'hr_outcome' },
  ];

  const gapItems = data.gap.items;

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title={`${e.full_name} (${e.employee_number})`}
        extra={
          <Button type="primary" onClick={() => navigate(`${COMPETENCY_BASE}/assessments/new?employee_id=${e.id}`)}>
            New assessment
          </Button>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, md: 3 }}>
          <Descriptions.Item label="Department">{e.department_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Job title">{e.job_title || '-'}</Descriptions.Item>
          <Descriptions.Item label="Reports to">{e.reporting_to_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Joined">{formatDate(e.join_date) || '-'}</Descriptions.Item>
          <Descriptions.Item label="Probation">{e.probation_months ? `${e.probation_months} months` : '-'}</Descriptions.Item>
          <Descriptions.Item label="Confirmation">
            {e.confirmation_status || '-'}
            {e.confirmation_date ? ` (${formatDate(e.confirmation_date)})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Job description">
            {data.gap.job_profile ? (
              <a onClick={() => navigate(`${COMPETENCY_BASE}/job-profiles/${data.gap.job_profile?.id}`)}>
                {data.gap.job_profile.code} - {data.gap.job_profile.title}
              </a>
            ) : (
              <Tag>Not assigned</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card>
        <Tabs
          items={[
            {
              key: 'requirements',
              label: `Requirements (${gapItems.filter((i) => i.status !== 'Met').length} open)`,
              children: data.gap.job_profile ? (
                <Table
                  rowKey="key"
                  size="small"
                  pagination={false}
                  dataSource={gapItems}
                  columns={[
                    { title: 'Type', dataIndex: 'requirement_type', width: 130 },
                    { title: 'Requirement', dataIndex: 'name' },
                    { title: 'Mandatory', dataIndex: 'mandatory', width: 100, render: (v) => (v ? 'Yes' : 'No') },
                    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={GAP_COLOR[v as keyof typeof GAP_COLOR]}>{v}</Tag> },
                    { title: 'Evidence', dataIndex: 'detail' },
                  ]}
                />
              ) : (
                <Alert type="info" showIcon message="Assign a job description to compare this employee against its requirements." />
              ),
            },
            {
              key: 'competencies',
              label: `Competencies (${data.competencies.length})`,
              children: (
                <>
                  {canCompetency && (
                    <Button icon={<PlusOutlined />} style={{ marginBottom: 12 }} onClick={() => openCompetency({ employee_id: e.id })}>
                      Add competency
                    </Button>
                  )}
                  <Table rowKey="id" size="small" dataSource={data.competencies} columns={competencyColumns} pagination={false} />
                </>
              ),
            },
            {
              key: 'trainings',
              label: `Training (${data.trainings.length})`,
              children: (
                <>
                  {canTraining && (
                    <Button icon={<PlusOutlined />} style={{ marginBottom: 12 }} onClick={() => openTraining({ employee_id: e.id })}>
                      Add training record
                    </Button>
                  )}
                  <Table rowKey="id" size="small" dataSource={data.trainings} columns={trainingColumns} pagination={{ defaultPageSize: 20 }} />
                </>
              ),
            },
            {
              key: 'certifications',
              label: `Certificates (${data.certifications.length})`,
              children: (
                <>
                  {canCert && (
                    <Button icon={<PlusOutlined />} style={{ marginBottom: 12 }} onClick={() => openCert({ employee_id: e.id })}>
                      Add certificate
                    </Button>
                  )}
                  <Table rowKey="id" size="small" dataSource={data.certifications} columns={certColumns} pagination={false} />
                </>
              ),
            },
            {
              key: 'assessments',
              label: `Assessments (${data.assessments.length})`,
              children: <Table rowKey="id" size="small" dataSource={data.assessments} columns={assessmentColumns} pagination={false} />,
            },
            {
              key: 'qualifications',
              label: `HR qualifications (${data.qualifications.length})`,
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={data.qualifications}
                  columns={[
                    { title: 'Type', dataIndex: 'qual_type' },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Institution', dataIndex: 'institution' },
                    { title: 'Level / grade', dataIndex: 'level_or_grade' },
                    { title: 'Issued', dataIndex: 'issued_on', render: (v) => formatDate(v) || '-' },
                    { title: 'Expires', dataIndex: 'expiry_date', render: (v) => formatDate(v) || '-' },
                    { title: 'Verified', dataIndex: 'is_verified', render: (v) => (Number(v) ? 'Yes' : 'No') },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={competency?.record?.id ? 'Edit competency' : 'Add competency'}
        open={!!competency}
        onCancel={() => setCompetency(null)}
        onOk={() => competencyForm.submit()}
        confirmLoading={saveCompetency.isPending}
      >
        <Form
          form={competencyForm}
          layout="vertical"
          onFinish={async (v) => {
            await saveCompetency.mutateAsync({ ...v, employee_id: e.id, last_assessed_date: fromDate(v.last_assessed_date) ?? undefined });
            setCompetency(null);
          }}
        >
          <Form.Item name="competency_code" label="Code" rules={[{ required: true }]}>
            <Input disabled={!!competency?.record?.id} />
          </Form.Item>
          <Form.Item name="competency_name" label="Competency" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="competency_category" label="Category">
            <Input />
          </Form.Item>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="required_level" label="Required level" rules={[{ required: true }]}>
              <Select options={COMPETENCY_LEVELS.map((l) => ({ value: l, label: l }))} />
            </Form.Item>
            <Form.Item name="current_level" label="Current level" rules={[{ required: true }]}>
              <Select options={['Not Assessed', ...COMPETENCY_LEVELS].map((l) => ({ value: l, label: l }))} />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="assessment_result" label="Result">
              <Select options={['Competent', 'Not Competent', 'Under Assessment'].map((l) => ({ value: l, label: l }))} />
            </Form.Item>
            <Form.Item name="last_assessed_date" label="Assessed on">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="assessment_method" label="Method">
            <Select allowClear options={['Training', 'Examination', 'Practical', 'Observation', 'Interview'].map((l) => ({ value: l, label: l }))} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={training?.record?.id ? 'Edit training record' : 'Add training record'}
        open={!!training}
        width={640}
        onCancel={() => setTraining(null)}
        onOk={() => trainingForm.submit()}
        confirmLoading={saveTraining.isPending}
      >
        <Form
          form={trainingForm}
          layout="vertical"
          onFinish={async (v) => {
            const start = fromDate(v.actual_start_date);
            const end = fromDate(v.actual_end_date) ?? start;
            await saveTraining.mutateAsync({
              id: training?.record?.id,
              data: {
                ...v,
                employee_id: e.id,
                actual_start_date: start ?? undefined,
                actual_end_date: end ?? undefined,
                scheduled_start_date: start ?? undefined,
                scheduled_end_date: end ?? undefined,
              },
            });
            setTraining(null);
          }}
        >
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="training_code" label="Code" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="training_name" label="Training" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="trainer_name" label="Trainer">
              <Input />
            </Form.Item>
            <Form.Item name="training_provider" label="Provider">
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="actual_start_date" label="Start" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="actual_end_date" label="End">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="training_duration_hours" label="Hours">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="status" label="Status">
              <Select options={['Scheduled', 'Completed', 'Cancelled', 'No Show'].map((l) => ({ value: l, label: l }))} />
            </Form.Item>
            <Form.Item name="result" label="Result">
              <Select options={['Pass', 'Fail', 'Pending', 'N/A'].map((l) => ({ value: l, label: l }))} />
            </Form.Item>
            <Form.Item name="certificate_number" label="Certificate no.">
              <Input />
            </Form.Item>
          </Space>
          <Form.Item name="training_notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={cert?.record?.id ? 'Edit certificate' : 'Add certificate'}
        open={!!cert}
        width={640}
        onCancel={() => setCert(null)}
        onOk={() => certForm.submit()}
        confirmLoading={saveCert.isPending}
      >
        <Form
          form={certForm}
          layout="vertical"
          onFinish={async (v) => {
            await saveCert.mutateAsync({
              id: cert?.record?.id,
              data: {
                ...v,
                employee_id: e.id,
                issue_date: fromDate(v.issue_date) ?? undefined,
                expiry_date: v.is_lifetime ? undefined : fromDate(v.expiry_date) ?? undefined,
              },
            });
            setCert(null);
          }}
        >
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="certification_code" label="Code" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="certification_type" label="Type">
              <Input placeholder="e.g. CSWIP 3.1, NEBOSH" />
            </Form.Item>
          </Space>
          <Form.Item name="certification_name" label="Certificate" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="issuing_body" label="Issued by" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="certificate_number" label="Certificate no." rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }} align="end">
            <Form.Item name="issue_date" label="Issued" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="expiry_date" label="Expires">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="is_lifetime" valuePropName="checked">
              <Checkbox>Lifetime</Checkbox>
            </Form.Item>
          </Space>
          <Form.Item name="status" label="Status">
            <Select options={['Active', 'Expired', 'Renewed', 'Revoked', 'Pending'].map((l) => ({ value: l, label: l }))} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompetencyEmployeePage;
